/**
 * back-in-stock.js
 *
 * Captures submits on every `.en-bis-form` and POSTs to the agent API:
 *   POST https://agent.getmyenergie.com/api/back-in-stock
 *   { sku, email, phone, name }
 *
 * No frameworks. Vanilla ES6+. Idempotent: safe to load once globally.
 */
(function () {
  'use strict';

  if (window.__enBackInStockBound) return;
  window.__enBackInStockBound = true;

  var ENDPOINT = 'https://agent.getmyenergie.com/api/back-in-stock';
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /** @param {string} sku @param {string} email */
  function storageKey(sku, email) {
    return 'bis-' + sku + '-' + email.toLowerCase();
  }

  /** @param {string} raw */
  function normalizePhone(raw) {
    if (!raw) return null;
    var digits = raw.replace(/[^0-9]/g, '');
    if (!digits) return null;
    return digits;
  }

  /**
   * @param {HTMLElement} root
   * @param {'idle'|'loading'|'success'|'duplicate'|'error'} state
   * @param {string} [text]
   */
  function setState(root, state, text) {
    var msg = root.querySelector('.en-bis-msg');
    var btn = root.querySelector('.en-bis-btn');
    var inputs = root.querySelectorAll('.en-bis-input');

    root.classList.remove('is-success', 'is-error', 'is-loading', 'is-duplicate');

    if (state === 'idle') {
      if (msg) {
        msg.hidden = true;
        msg.textContent = '';
        msg.removeAttribute('data-state');
      }
      if (btn) {
        btn.disabled = false;
        btn.textContent = 'Avísame cuando vuelva';
      }
      inputs.forEach(function (i) { i.removeAttribute('aria-invalid'); });
      return;
    }

    if (state === 'loading') {
      root.classList.add('is-loading');
      if (btn) {
        btn.disabled = true;
        btn.textContent = 'Enviando…';
      }
      if (msg) { msg.hidden = true; msg.textContent = ''; }
      return;
    }

    if (state === 'success' || state === 'duplicate') {
      root.classList.add(state === 'duplicate' ? 'is-duplicate' : 'is-success');
      if (msg) {
        msg.hidden = false;
        msg.setAttribute('data-state', state);
        msg.textContent = text || (state === 'duplicate'
          ? 'Ya estás en la lista. Te avisamos en cuanto regrese.'
          : 'Listo. Te avisamos en cuanto regrese a stock.');
      }
      if (btn) {
        btn.disabled = true;
        btn.textContent = state === 'duplicate' ? '✓ Ya en la lista' : '✓ Apuntado';
      }
      return;
    }

    if (state === 'error') {
      root.classList.add('is-error');
      if (msg) {
        msg.hidden = false;
        msg.setAttribute('data-state', 'error');
        msg.textContent = text || 'No pudimos registrarte. Intenta de nuevo.';
      }
      if (btn) {
        btn.disabled = false;
        btn.textContent = 'Avísame cuando vuelva';
      }
      return;
    }
  }

  /** @param {SubmitEvent} ev */
  function onSubmit(ev) {
    var form = /** @type {HTMLFormElement} */ (ev.target);
    if (!form || !form.matches || !form.matches('.en-bis-form')) return;
    ev.preventDefault();

    var root = form.closest('.en-bis');
    if (!root) return;
    var sku = root.getAttribute('data-sku') || '';
    var productName = root.getAttribute('data-product-name') || '';
    if (!sku) {
      setState(root, 'error', 'SKU no configurado.');
      return;
    }

    var emailInput = /** @type {HTMLInputElement|null} */ (form.querySelector('input[name="email"]'));
    var phoneInput = /** @type {HTMLInputElement|null} */ (form.querySelector('input[name="phone"]'));
    var email = (emailInput && emailInput.value || '').trim();
    var phoneRaw = (phoneInput && phoneInput.value || '').trim();

    // Validate email
    if (!EMAIL_RE.test(email)) {
      if (emailInput) emailInput.setAttribute('aria-invalid', 'true');
      setState(root, 'error', 'Ingresa un correo válido.');
      if (emailInput) emailInput.focus();
      return;
    }
    if (emailInput) emailInput.removeAttribute('aria-invalid');

    // Validate phone (optional)
    var phone = null;
    if (phoneRaw) {
      var digits = normalizePhone(phoneRaw);
      if (!digits || digits.length < 10) {
        if (phoneInput) phoneInput.setAttribute('aria-invalid', 'true');
        setState(root, 'error', 'WhatsApp debe tener al menos 10 dígitos.');
        if (phoneInput) phoneInput.focus();
        return;
      }
      phone = digits;
      if (phoneInput) phoneInput.removeAttribute('aria-invalid');
    }

    // Local dedupe (same session)
    try {
      var key = storageKey(sku, email);
      if (window.localStorage && window.localStorage.getItem(key)) {
        setState(root, 'duplicate');
        return;
      }
    } catch (_) { /* storage unavailable, ignore */ }

    setState(root, 'loading');

    var payload = {
      sku: sku,
      email: email,
      phone: phone,
      name: productName || null
    };

    fetch(ENDPOINT, {
      method: 'POST',
      mode: 'cors',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (res) {
        return res.json().then(function (data) { return { status: res.status, data: data }; })
          .catch(function () { return { status: res.status, data: {} }; });
      })
      .then(function (out) {
        var status = out.status;
        var data = out.data || {};
        if (status >= 200 && status < 300 && data.ok) {
          try {
            var k = storageKey(sku, email);
            if (window.localStorage) window.localStorage.setItem(k, String(Date.now()));
          } catch (_) { /* ignore */ }
          if (data.duplicate) {
            setState(root, 'duplicate', data.message || undefined);
          } else {
            setState(root, 'success', data.message || undefined);
          }
          // Lightweight analytics signal (optional, no-op if dataLayer absent)
          try {
            if (window.dataLayer && typeof window.dataLayer.push === 'function') {
              window.dataLayer.push({
                event: 'back_in_stock_subscribe',
                sku: sku,
                product_name: productName || undefined,
                duplicate: !!data.duplicate
              });
            }
          } catch (_) { /* ignore */ }
          return;
        }
        var detail = (data && data.detail) || (data && data.message) || 'No pudimos registrarte.';
        setState(root, 'error', String(detail));
      })
      .catch(function () {
        setState(root, 'error', 'Error de red. Intenta de nuevo.');
      });
  }

  function init() {
    document.addEventListener('submit', onSubmit, false);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
